import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ethers } from 'ethers6';
import { useAccount, useWalletClient } from 'wagmi';
import { uploadBase64ImageToStorage, uploadBlobToStorage, STORAGE_BUCKETS, supabase, trackWidgetDeployment } from '../../utils/supabase';
import { generateTokenCard } from '../../utils/generateTokenCard';
import { useCreateToken } from '../../hooks/useSupabaseTokens';
import { deployTokenGasless, waitForTaskCompletion, getTransactionReceiptWithFailover } from '../../utils/gelatoRelay';
import { validateTokenContent } from '../../utils/blacklist';
import { getEthUsdPrice } from '../../utils/ethPrice';
import { deployWithTemporaryWallet } from '../../utils/temporaryWalletDeploy';
import { getFactoryABI, getFactoryAddress } from '../../utils/velodromeFactoryABI';
import { config } from '../../utils/config';
import SliderCaptcha from '../shared/modals/SliderCaptcha';
import PrivateKeyModal from '../shared/modals/PrivateKeyModal';
import { ShareModal } from '../shared/modals/ShareModal';

// Ink V2 factory address (with ERC-2771 Gelato support and multi-DEX)
const FACTORY_ADDRESS = '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60';

// V2 Contract ABI (Updated for multi-DEX support)
const FACTORY_ABI = [
  {"inputs":[{"internalType":"address[]","name":"_initialNPMs","type":"address[]"},{"internalType":"address[]","name":"_v3Factories","type":"address[]"},{"internalType":"address","name":"_poolManager","type":"address"},{"internalType":"address","name":"_locker","type":"address"},{"internalType":"address","name":"trustedForwarder_","type":"address"}],"stateMutability":"nonpayable","type":"constructor"},
  {"anonymous":false,"inputs":[{"indexed":false,"internalType":"uint256","name":"tokenId","type":"uint256"},{"indexed":true,"internalType":"address","name":"creator","type":"address"},{"indexed":false,"internalType":"address","name":"npm","type":"address"}],"name":"LiquidityMinted","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"oldLocker","type":"address"},{"indexed":false,"internalType":"address","name":"newLocker","type":"address"}],"name":"LockerUpdated","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":false,"internalType":"uint256","name":"tokenId","type":"uint256"},{"indexed":true,"internalType":"address","name":"creator","type":"address"}],"name":"NFTLocked","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"npm","type":"address"},{"indexed":true,"internalType":"address","name":"v3Factory","type":"address"},{"indexed":false,"internalType":"bool","name":"whitelisted","type":"bool"}],"name":"NPMWhitelisted","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"npm","type":"address"},{"indexed":false,"internalType":"address","name":"oldFactory","type":"address"},{"indexed":false,"internalType":"address","name":"newFactory","type":"address"}],"name":"NPMV3FactoryUpdated","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"pool","type":"address"},{"indexed":false,"internalType":"address","name":"npm","type":"address"}],"name":"PoolInitialized","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"oldManager","type":"address"},{"indexed":false,"internalType":"address","name":"newManager","type":"address"}],"name":"PoolManagerUpdated","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"tokenAddress","type":"address"},{"indexed":false,"internalType":"string","name":"name","type":"string"},{"indexed":false,"internalType":"string","name":"symbol","type":"string"},{"indexed":true,"internalType":"address","name":"creator","type":"address"},{"indexed":true,"internalType":"address","name":"npm","type":"address"},{"indexed":false,"internalType":"uint256","name":"totalDeployed","type":"uint256"}],"name":"TokenDeployed","type":"event"},
  {"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"forwarder","type":"address"}],"name":"TrustedForwarderSet","type":"event"},
  {"inputs":[],"name":"FEE_TIER","outputs":[{"internalType":"uint24","name":"","type":"uint24"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"WETH","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"creatorTokenCount","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"creator","type":"address"}],"name":"getCreatorNFTCount","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"creator","type":"address"}],"name":"getCreatorNFTs","outputs":[{"internalType":"uint256[]","name":"","type":"uint256[]"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"creator","type":"address"}],"name":"getCreatorTokenCount","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"getLocker","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"uint256","name":"tokenId","type":"uint256"}],"name":"getNFTCreator","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"getPoolManager","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"getTotalTokensDeployed","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"getTrustedForwarder","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"npm","type":"address"}],"name":"getV3FactoryForNPM","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"getWhitelistedNPMs","outputs":[{"internalType":"address[]","name":"","type":"address[]"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"npm","type":"address"}],"name":"isNPMWhitelisted","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"forwarder","type":"address"}],"name":"isTrustedForwarder","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"locker","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"mavrkPoolManager","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"string","name":"_name","type":"string"},{"internalType":"string","name":"_symbol","type":"string"},{"internalType":"address","name":"npm","type":"address"}],"name":"newMavrkToken","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"nftCreators","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"npmList","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"npmToV3Factory","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[],"name":"owner","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"npm","type":"address"}],"name":"removeNPM","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"address","name":"newLocker","type":"address"}],"name":"setLockerAddress","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"address","name":"newPoolManager","type":"address"}],"name":"setPoolManager","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"address","name":"forwarder","type":"address"}],"name":"setTrustedForwarder","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[],"name":"totalTokensDeployed","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
  {"inputs":[{"internalType":"address","name":"newOwner","type":"address"}],"name":"transferOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"address","name":"npm","type":"address"},{"internalType":"address","name":"v3Factory","type":"address"}],"name":"updateNPMV3Factory","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"address","name":"npm","type":"address"},{"internalType":"address","name":"v3Factory","type":"address"}],"name":"whitelistNPM","outputs":[],"stateMutability":"nonpayable","type":"function"},
  {"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"whitelistedNPMs","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"}
];

// Ink router and contract constants for initial buy
const WETH_ADDRESS = "0x4200000000000000000000000000000000000006";

// Router ABI for swap functions (standard Uniswap V3)
const ROUTER_ABI = [
  "function exactInputSingle((address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96)) external payable returns (uint256 amountOut)",
];

// NPM addresses for DEX selection
const NPM_ADDRESSES = {
  PROTOFIRE: '0xC0836E5B058BBE22ae2266e1AC488A1A0fD8DCE8',
  INKYSWAP: '0x1921C0c86494d25F90E9a535327ec989372a860f'
} as const;

// Swap router addresses for each DEX
const SWAP_ROUTER_ADDRESSES = {
  PROTOFIRE: '0x177778F19E89dD1012BdBe603F144088A95C4B53',
  INKYSWAP: '0xf4fBa8170494BFe11fE21d9D7C9780B1FFC9a015'
} as const;

const PageWrapper = styled.div`
  width: 100%;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 700px) {
    padding: 20px 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: var(--mv-font, #ffffff);
  text-align: center;
  margin-bottom: 24px;
  
  @media (max-width: 700px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`;

const CreateContainer = styled.div<{ $asPage?: boolean }>`
  background: var(--mv-bg, #000000);
  color: var(--mv-font, #ffffff);
  border-radius: 20px;
  padding: 32px;
  box-shadow: none;
  transition: none;
  border: none;
  width: ${props => props.$asPage ? '100%' : '80%'};
  max-width: ${props => props.$asPage ? '900px' : '650px'};
  margin: 0 auto;
  backdrop-filter: blur(10px);
  
  @media (max-width: 1200px) {
    max-width: 90%;
  }
  
  @media (max-width: 700px) {
    padding: 16px 12px;
    border-radius: 16px;
    max-width: 100%;
    margin: 0;
    width: 100%;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  @media (max-width: 700px) {
    gap: 2px;
  }
`;



const FormGroup = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${p => p.$compact ? '6px' : '8px'};
  margin-bottom: ${p => p.$compact ? '6px' : '8px'};

  @media (max-width: 700px) {
    gap: ${p => p.$compact ? '4px' : '6px'};
    margin-bottom: ${p => p.$compact ? '4px' : '6px'};
  }
`;

const HorizontalFormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
  
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Label = styled.label<{ $compact?: boolean }>`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: ${p => p.$compact ? '10px' : '11px'};
  color: var(--mv-font, #ffffff);
  display: flex;
  align-items: center;
  gap: 5px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: ${p => p.$compact ? '4px' : '6px'};

  &.required::after {
    content: '*';
    color: var(--mv-primary, #9535fc);
    margin-left: 3px;
    font-size: 14px;
  }
  
  @media (max-width: 700px) {
    font-size: ${p => p.$compact ? '9px' : '9px'};
    margin-bottom: ${p => p.$compact ? '3px' : '3px'};
  }
`;

const Input = styled.input<{ $compact?: boolean }>`
  background: var(--mv-input-bg, #000000);
  border: 1px solid #333333;
  border-radius: 8px;
  padding: ${p => p.$compact ? '9px 10px' : '12px 14px'};
  color: var(--mv-font, #ffffff);
  font-family: 'Inter', sans-serif;
  font-size: ${p => p.$compact ? '13px' : '14px'};
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;
  min-width: 0;

  &:focus {
    border-color: #444444;
    background: var(--mv-input-bg, #000000);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary, #a855f7) 10%, transparent);
  }

  &::placeholder {
    color: #666666;
  }
  
  @media (max-width: 700px) {
    padding: ${p => p.$compact ? '8px 10px' : '10px 12px'};
    font-size: ${p => p.$compact ? '12px' : '13px'};
  }
`;

const TextArea = styled.textarea<{ $compact?: boolean }>`
  background: var(--mv-input-bg, #000000);
  border: 1px solid #333333;
  border-radius: 8px;
  padding: ${p => p.$compact ? '9px 10px' : '12px 14px'};
  color: var(--mv-font, #ffffff);
  font-family: 'Inter', sans-serif;
  font-size: ${p => p.$compact ? '13px' : '14px'};
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;
  min-height: ${p => p.$compact ? '60px' : '80px'};
  resize: vertical;

  &:focus {
    border-color: #444444;
    background: var(--mv-input-bg, #000000);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary, #a855f7) 10%, transparent);
  }

  &::placeholder {
    color: #666666;
  }
  
  @media (max-width: 700px) {
    padding: 10px 12px;
    font-size: 12px;
    min-height: 50px;
  }
`;

const FileUploadContainer = styled.div`
  background: var(--mv-input-bg, #000000);
  border: 2px solid #444444;
  border-radius: 3px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.1s linear, box-shadow 0.1s linear;
  box-shadow: 2px 2px #fffeff;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #fffeff;
    box-shadow: 3px 3px var(--mv-primary, #9535fc);
  }

  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  .upload-content {
    pointer-events: none;
  }

  .upload-content i {
    font-size: 1.3em;
    color: var(--mv-primary, #9535fc);
    margin-bottom: 4px;
  }

  .upload-content p {
    margin: 2px 0;
    color: #fffeff;
    font-family: 'Inter', sans-serif;
    font-size: 11px;
  }

  .upload-content .small-text {
    font-size: 9px;
    color: #bdbdbd;
  }

  .upload-content .file-info {
    font-size: 9px;
    color: var(--mv-primary, #9535fc);
    margin-top: 4px;
  }

  img.preview-image {
    max-width: 100%;
    max-height: 70px;
    border-radius: 5px;
    margin-top: 4px;
  }
`;

const DeployButton = styled.button`
  background: linear-gradient(135deg, var(--mv-primary, #a855f7) 0%, color-mix(in srgb, var(--mv-primary, #7c3aed) 80%, black) 100%);
  color: #ffffff;
  padding: 16px 32px;
  border: none;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  box-shadow: 0 4px 16px #333333;
  margin-top: 16px;

  @media (max-width: 700px) {
    font-size: 14px;
    padding: 14px 24px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px color-mix(in srgb, var(--mv-primary, #a855f7) 50%, transparent);
    background: linear-gradient(135deg, color-mix(in srgb, var(--mv-primary, #b66bff) 100%, white 10%) 0%, color-mix(in srgb, var(--mv-primary, #8b5cf6) 80%, black) 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: rgba(80, 80, 90, 0.4);
    color: #666666;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Note = styled.p`
  font-size: 9px;
  color: var(--mv-font, #fffeff);
  text-align: center;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 6px;
  border-radius: 5px;
  margin: 6px 0;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  
  ${({ type }) => {
    switch (type) {
      case 'success':
        return `
          background: rgba(33, 201, 94, 0.12);
          border: 1px solid #21C95E;
          color: #21C95E;
        `;
      case 'error':
        return `
          background: rgba(255, 89, 60, 0.12);
          border: 1px solid #FF593C;
          color: #FF593C;
        `;
      case 'info':
        return `
          background: color-mix(in srgb, var(--mv-primary, #9535fc) 12%, transparent);
          border: 1px solid var(--mv-primary, #9535fc);
          color: var(--mv-primary, #9535fc);
        `;
    }
  }}
`;

const EnhancedSuccessMessage = styled.div`
  padding: 12px;
  border-radius: 8px;
  margin: 8px 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  background: rgba(33, 201, 94, 0.12);
  border: 1px solid #21C95E;
  color: #21C95E;
  line-height: 1.4;
`;

const SuccessLink = styled.a`
  color: #21C95E;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    color: #1a9d4a;
  }
`;

const ContractAddress = styled.span`
  font-family: 'Courier New', monospace;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  user-select: all;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(12, 8, 20, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinnerBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Hourglass = styled.div`
  width: 64px;
  height: 64px;
  border: 6px solid #444444;
  border-top: 6px solid #fff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 18px;
  box-shadow: 0 0 16px #9535fc88;
`;

const SpinnerText = styled.div`
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 15px;
  text-align: center;
  text-shadow: 0 2px 8px #000;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 700px) {
    font-size: 13px;
    letter-spacing: 0.3px;
  }
`;

const ProgressMessage = styled.div`
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-weight: 400;
  font-size: 12px;
  text-align: center;
  margin-top: 12px;
  text-shadow: 0 0 8px #00ff0088;
  white-space: pre-line;
  max-width: 400px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalBox = styled.div`
  background: radial-gradient(circle at 50% 100%, rgba(70, 85, 110, 0.5) 0%, transparent 60%),
              radial-gradient(circle at 50% 100%, rgba(99, 102, 241, 0.4) 0%, transparent 70%),
              radial-gradient(circle at 50% 100%, rgba(181, 184, 208, 0.3) 0%, transparent 80%),
              #000000;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(168, 85, 247, 0.1);
  border: 1px solid #333333;
  padding: 24px;
  min-width: 320px;
  max-width: 90vw;
  max-height: 85vh;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  backdrop-filter: blur(10px);
  
  @media (max-width: 700px) {
    max-height: 85vh;
    padding: 16px 12px;
    min-width: 280px;
    max-width: 95vw;
  }
`;
const ModalTitle = styled.h2`
  font-size: 1.3rem;
  margin-bottom: 16px;
  font-weight: 700;
`;
const ModalText = styled.p`
  font-size: 1rem;
  margin-bottom: 24px;
  text-align: center;
`;
const ModalButtonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;
const ModalButton = styled.button`
  background: #00E8A2;
  color: #181A20;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  &:hover:not(:disabled) { background: #00c98a; }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ModalCancel = styled(ModalButton)`
  background: #23262F;
  color: #fff;
  &:hover { background: #393C49; }
`;

const DeploymentModeContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  
  @media (max-width: 700px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const VerticalOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
`;

const ModeOption = styled.div<{ selected: boolean; $compact?: boolean }>`
  flex: 1;
  background: var(--mv-input-bg, #000000);
  border: 2px solid ${props => props.selected ? 'var(--mv-primary, #9535fc)' : '#444'};
  border-radius: 6px;
  padding: ${p => p.$compact ? '6px 10px' : '8px 12px'};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;
  
  &:hover {
    border-color: #444444;
    background: var(--mv-input-bg, #000000);
  }

  @media (max-width: 700px) {
    padding: ${p => p.$compact ? '5px 8px' : '6px 10px'};
  }
`;

const ModeTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--mv-font, #ffffff);
  font-family: 'Inter', sans-serif;
  text-align: center;
`;

const ModeDescription = styled.div`
  font-size: 11px;
  color: #b0b0b0;
  font-family: 'Inter', sans-serif;
  line-height: 1.4;
`;

const ModeBadge = styled.span`
  display: inline-block;
  background: #10b981;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
`;

const CategoryButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  width: 100%;
  
  @media (max-width: 700px) {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 6px;
  }
`;

const CategoryButton = styled.button<{ selected: boolean }>`
  padding: 8px 12px;
  background: var(--mv-input-bg, #000000);
  border: 1px solid ${props => props.selected ? 'var(--mv-primary, #9535fc)' : '#333333'};
  border-radius: 6px;
  color: ${props => props.selected ? '#ffffff' : '#ffffff'};
  font-size: 11px;
  font-weight: ${props => props.selected ? '600' : '500'};
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    border-color: #444444;
    background: var(--mv-input-bg, #000000);
    color: #ffffff;
  }
  
  &:focus,
  &:focus-visible {
    outline: none;
    border-color: #444444;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary, #a855f7) 10%, transparent);
  }
  
  @media (max-width: 700px) {
    padding: 6px 10px;
    font-size: 10px;
  }
`;

const UploadRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: start;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }
`;

const UploadColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
  
  @media (max-width: 700px) {
    margin-bottom: 4px;
  }
`;

const CenteredLogoContainer = styled.div`
  width: 100px;
  height: 100px;
  
  @media (max-width: 700px) {
    width: 80px;
    height: 80px;
  }
`;

const BannerUploadWrapper = styled.div`
  width: 100%;
`;

const BannerFileUploadContainer = styled.div`
  background: var(--mv-input-bg, #000000);
  border: 2px dashed #444444;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  /* Fixed height to match logo container */
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  &:hover {
    border-color: #444444;
    background: rgba(168, 85, 247, 0.1);
  }

  .upload-content {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .upload-content i {
    font-size: 2em;
    color: #a855f7;
  }

  .upload-content p {
    margin: 0;
    color: #ffffff;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
  }

  .upload-content .small-text {
    font-size: 10px;
    color: #9ca3af;
    font-weight: 400;
  }

  img.preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
  
  @media (max-width: 700px) {
    height: 80px;
    padding: 8px;
  }
`;

const LogoFileUploadContainer = styled.div`
  background: var(--mv-input-bg, #000000);
  border: 2px dashed #444444;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #444444;
    background: rgba(168, 85, 247, 0.1);
  }

  .upload-content {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .upload-content i {
    font-size: 2em;
    color: #a855f7;
  }

  .upload-content p {
    margin: 0;
    color: #ffffff;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
  }

  .upload-content .small-text {
    font-size: 10px;
    color: #9ca3af;
    font-weight: 400;
  }

  img.preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
  
  @media (max-width: 700px) {
    padding: 10px;
    
    .upload-content i {
      font-size: 1.5em;
    }
    
    .upload-content p {
      font-size: 10px;
    }
    
    .upload-content .small-text {
      font-size: 8px;
    }
  }
`;

const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  /* Tablet: keep two columns to reduce scroll */
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  /* Very small screens: fall back to single column */
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const VerticalStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RightPaneStack = styled.div`
  grid-column: 2 / 4;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 1024px) {
    grid-column: 1 / 2;
  }
`;

const MostUsedBadge = styled.span`
  display: inline-block;
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.3px;
`;

const NewBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.5px;
  font-style: italic;
  text-transform: uppercase;
  animation: pulse 2s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
    }
    50% {
      opacity: 0.8;
      box-shadow: 0 0 20px rgba(168, 85, 247, 0.8);
    }
  }
`;

const MarketCapDropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MarketCapSelected = styled.div`
  background: #2a2a2a;
  border: 2px solid #444;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #444444;
    background: #333;
  }

  @media (max-width: 700px) {
    padding: 10px 12px;
  }
`;

const MarketCapSelectedText = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;

  @media (max-width: 700px) {
    font-size: 12px;
  }
`;

const DropdownIcon = styled.span<{ isOpen: boolean }>`
  color: var(--mv-primary, #a855f7);
  font-size: 12px;
  transition: transform 0.2s;
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
`;

const MarketCapDropdownList = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #2a2a2a;
  border: 2px solid #444444;
  border-radius: 8px;
  overflow: hidden;
  max-height: ${props => props.isOpen ? '200px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  z-index: 100;
`;

const MarketCapDropdownItem = styled.div<{ selected: boolean; disabled?: boolean }>`
  padding: 12px 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background: ${props => {
    if (props.disabled) return '#000000';
    return props.selected ? 'rgba(149, 53, 252, 0.2)' : 'transparent';
  }};
  color: ${props => props.disabled ? '#666' : '#ffffff'};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    background: ${props => {
      if (props.disabled) return '#000000';
      return props.selected ? 'rgba(149, 53, 252, 0.3)' : 'rgba(149, 53, 252, 0.15)';
    }};
  }

  @media (max-width: 700px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const CollapsibleSection = styled.div`
  border: 1px solid #333333;
  border-radius: 8px;
  overflow: hidden;
  
  &:focus-within {
    border-color: #444444;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary, #a855f7) 10%, transparent);
  }
`;

const CollapsibleHeader = styled.div`
  background: var(--mv-input-bg, #000000);
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s;
  
  &:hover {
    background: var(--mv-input-bg, #000000);
  }
  
  @media (max-width: 700px) {
    padding: 8px 10px;
  }
`;

const CollapsibleTitle = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #fffeff;
  letter-spacing: 0.5px;
`;

const CollapsibleIcon = styled.span<{ isOpen: boolean }>`
  color: var(--mv-primary, #a855f7);
  font-size: 14px;
  transition: transform 0.2s;
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
`;

const CollapsibleContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: var(--mv-input-bg, #000000);
`;

const CollapsibleInner = styled.div`
  padding: 10px;
`;

interface CreateTokenFormData {
  name: string;
  symbol: string;
  description: string;
  logo: string;
  coverPhoto: string;
  website: string;
  twitter: string;
  telegram: string;
  discord: string;
  initialBuyAmount: string;
  dexSelection: 'protofire' | 'inkyswap';
  category: 'Meme' | 'DeFi' | 'Utility' | 'Gaming & Metaverse' | 'SoFi' | 'Creator Coins' | 'AI & Tech' | 'DAO' | 'Experimental/Art' | 'Kraken Girls';
}

interface CreateTokenFormProps {
  isVisible: boolean;
  onClose: () => void;
  asPage?: boolean; // If true, renders as a page without modal wrapper
  allowedDex?: 'protofire' | 'inkyswap' | 'both';
  compact?: boolean;
  selectedChain?: 'ink' | 'eth' | 'mon' | 'sol';
}

export const CreateTokenForm: React.FC<CreateTokenFormProps> = ({ isVisible, onClose, asPage = false, allowedDex = 'both', compact = false, selectedChain = 'ink' }) => {
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [enhancedSuccessMessage, setEnhancedSuccessMessage] = useState<{ tokenName: string; contractAddress: string; dextoolsUrl: string } | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalData, setShareModalData] = useState<{ 
    name: string; 
    symbol: string; 
    contractAddress: string;
    logo_url?: string | null;
    description?: string | null;
    deployer_address?: string;
    created_at?: string;
    pool_address?: string | null;
    nft_token_id?: string | null;
    card_url?: string | null;
    privateKey?: string;
    walletAddress?: string;
  } | null>(null);
  
  // Supabase hooks
  const { createToken } = useCreateToken();
  
  const [formData, setFormData] = useState<CreateTokenFormData>({
    name: '',
    symbol: '',
    description: '',
    logo: '',
    coverPhoto: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: '',
    initialBuyAmount: '0.000001',
    dexSelection: 'protofire', // Default to Protofire
    category: 'Meme', // Default category
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string>('');
  // Apply widget customization from URL params (borderOutlines, backgroundColor, buttonBackground, inputBackground)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const outlines = params.get('borderOutlines') || params.get('primaryColor');
      const bg = params.get('backgroundColor');
      const btn = params.get('buttonBackground');
      const inp = params.get('inputBackground');
      const font = params.get('fontColor');

      const root = document.documentElement;
      if (outlines && /^#?[0-9A-Fa-f]{6}$/.test(outlines.replace('#', ''))) {
        const hex = outlines.startsWith('#') ? outlines : `#${outlines}`;
        root.style.setProperty('--mv-primary', hex);
      }
      if (bg && /^#?[0-9A-Fa-f]{6}$/.test(bg.replace('#', ''))) {
        const hex = bg.startsWith('#') ? bg : `#${bg}`;
        root.style.setProperty('--mv-bg', hex);
      }
      if (btn && /^#?[0-9A-Fa-f]{6}$/.test(btn.replace('#', ''))) {
        const hex = btn.startsWith('#') ? btn : `#${btn}`;
        root.style.setProperty('--mv-button-bg', hex);
      }
      if (inp && /^#?[0-9A-Fa-f]{6}$/.test(inp.replace('#', ''))) {
        const hex = inp.startsWith('#') ? inp : `#${inp}`;
        root.style.setProperty('--mv-input-bg', hex);
      }
      if (font && /^#?[0-9A-Fa-f]{6}$/.test(font.replace('#', ''))) {
        const hex = font.startsWith('#') ? font : `#${font}`;
        root.style.setProperty('--mv-font', hex);
      }
    } catch {
      // no-op
    }
  }, []);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  // Ink-only configuration
  
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);

  const [showFirstBuyModal, setShowFirstBuyModal] = useState(false);
  const [firstBuyEth, setFirstBuyEth] = useState(0.000001);
  const [selectedBuyPercentage, setSelectedBuyPercentage] = useState<1 | 2 | 3 | null>(null);
  const [isSocialLinksOpen, setIsSocialLinksOpen] = useState(false);
  const [isCategorySectionOpen, setIsCategorySectionOpen] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(!compact);
  const [showSocialsSheet, setShowSocialsSheet] = useState(false);
  
  // Deployment mode state
  const [deploymentMode, setDeploymentMode] = useState<'gasless' | 'self-funded'>('gasless'); // always gasless
  const lpProvider = 'relay'; // Always use Relay LP provider
  const [isPlatformDeploying, setIsPlatformDeploying] = useState(false);
  const [isGaslessDeploying, setIsGaslessDeploying] = useState(false);
  const [isSelfFundedDeploying, setIsSelfFundedDeploying] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState('');

  // CAPTCHA state for platform deployments (hidden mode when no wallet)
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  // Private Key Modal state for temporary wallet deployments
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [deploymentPrivateKey, setDeploymentPrivateKey] = useState('');
  const [deploymentWalletAddress, setDeploymentWalletAddress] = useState('');
  const [deploymentTokenAddress, setDeploymentTokenAddress] = useState('');

  // ETH price state for market cap calculation
  const [ethPrice, setEthPrice] = useState<number>(4000); // Default fallback

  // Fetch ETH price on mount
  useEffect(() => {
    const fetchEthPrice = async () => {
      try {
        const price = await getEthUsdPrice();
        setEthPrice(price);
      } catch (error) {
        console.error('Failed to fetch ETH price:', error);
        // Keep default fallback of $4000
      }
    };
    fetchEthPrice();
  }, []);

  // Collapse description by default on small screens or when in compact mode (widget)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isMobile = window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
      setIsDescriptionOpen(!(isMobile || compact));
    }
  }, [compact]);

  // Ensure socials start closed in compact mode
  useEffect(() => {
    if (compact) setIsSocialLinksOpen(false);
  }, [compact]);

  // Enforce allowed DEX if restricted via props
  useEffect(() => {
    if (allowedDex === 'protofire' && formData.dexSelection !== 'protofire') {
      setFormData(prev => ({ ...prev, dexSelection: 'protofire' }));
    }
    if (allowedDex === 'inkyswap' && formData.dexSelection !== 'inkyswap') {
      setFormData(prev => ({ ...prev, dexSelection: 'inkyswap' }));
    }
  }, [allowedDex]);

  // Calculate realistic token estimates (standard market cap in V2)
  // Calculate ETH needed for X% of supply
  const calculateEthForPercentage = (percentage: number, pricePerTokenETH?: number): number => {
    const totalSupply = 1000000000; // 1B tokens
    const standardMarketCap = 0.5; // 0.5 ETH standard (fallback)
    
    // Use provided price or calculate from standard market cap
    const pricePerToken = pricePerTokenETH ?? (standardMarketCap / totalSupply);
    
    const tokensToBuy = (totalSupply * percentage) / 100;
    const ethNeeded = tokensToBuy * pricePerToken;
    
    return ethNeeded;
  };

  const estimateFirstBuyTokens = (ethInput: number, percentage?: number) => {
    // If percentage is provided, calculate tokens directly from percentage
    // This ensures exact percentage amounts (1% = 10M, 2% = 20M, 3% = 30M)
    if (percentage !== undefined) {
      const totalSupply = 1000000000; // 1B tokens
      return Math.floor((totalSupply * percentage) / 100);
    }
    
    // Fallback: Calculate from ETH input (for backwards compatibility)
    // V2 uses standard market cap for all tokens
    const standardMarketCap = 0.5; // 0.5 ETH standard
    
    const totalSupply = 1000000000; // 1B tokens
    const pricePerToken = standardMarketCap / totalSupply; // ETH per token
    const baseTokens = ethInput / pricePerToken;
    
    // Apply realistic price impact curve
    let impactMultiplier = 1.0;
    if (ethInput <= 0.01) {
      impactMultiplier = 1.0; // No impact for small buys
    } else if (ethInput <= 0.1) {
      impactMultiplier = 0.9; // 10% impact
    } else if (ethInput <= 0.5) {
      impactMultiplier = 0.8; // 20% impact
    } else {
      impactMultiplier = 0.7; // 30% impact for large buys
    }
    
    return Math.floor(baseTokens * impactMultiplier);
  };

  // Helper function to get standard market cap display
  const getMarketCapDisplay = (): string => {
    const standardMarketCap = ethPrice * 0.5; // 0.5 ETH standard
    // Format with k for thousands
    if (standardMarketCap >= 1000) {
      const marketCapInK = standardMarketCap / 1000;
      const decimal = marketCapInK % 1;
      
      // If .9, round up to next whole number
      if (decimal >= 0.9) {
        return `$${Math.ceil(marketCapInK)}k`;
      }
      
      // If it's a whole number (.0), show without decimal
      if (decimal < 0.05) {
        return `$${Math.floor(marketCapInK)}k`;
      }
      
      // Otherwise show one decimal place
      return `$${marketCapInK.toFixed(1)}k`;
    }
    return `$${standardMarketCap.toFixed(0)}`;
  };

  // Handle CAPTCHA success
  const handleCaptchaSuccess = () => {
    console.log('=�Ļ CAPTCHA SUCCESS - Setting captchaVerified to true');
    setCaptchaVerified(true);
    setShowCaptcha(false); // Hide CAPTCHA immediately after success
    // Automatically proceed with deployment after verification
    setTimeout(() => {
      console.log('=�Ļ CAPTCHA SUCCESS - About to call proceedWithPlatformDeployment, captchaVerified:', captchaVerified);
      proceedWithPlatformDeployment(true); // Pass true directly
    }, 500);
  };

  // Handle CAPTCHA failure
  const handleCaptchaFail = () => {
    displayResult('CAPTCHA VERIFICATION FAILED. PLEASE TRY AGAIN.', 'error');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSymbolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, symbol: e.target.value.toUpperCase() }));
  };

  const resizeImage = async (file: File, maxSize: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function(e) {
        const img = new Image();
        img.onload = function() {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error("COULD NOT GET CANVAS CONTEXT."));
              return;
            }

            if (maxSize === 100 || maxSize === 1024) { // 100px for small icons, 1024px for high quality logos
              // Only resize down if larger than maxSize, keep original if smaller
              if (img.width > maxSize || img.height > maxSize) {
                const ratio = Math.min(maxSize / img.width, maxSize / img.height);
                canvas.width = img.width * ratio;
                canvas.height = img.height * ratio;
              } else {
                // Keep original size if already smaller than maxSize
                canvas.width = img.width;
                canvas.height = img.height;
              }
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            } else if (maxSize === 1360) { 
              const targetWidth = 1360;
              const targetHeight = 320;

              const imgAspectRatio = img.width / img.height;
              const canvasAspectRatio = targetWidth / targetHeight;

              let sx = 0, sy = 0; 
              let sWidth = img.width, sHeight = img.height; 
              let dx = 0, dy = 0; 
              let dWidth = targetWidth, dHeight = targetHeight; 
              
              if (imgAspectRatio > canvasAspectRatio) {
                sHeight = img.height; 
                sWidth = img.height * canvasAspectRatio; 
                sx = (img.width - sWidth) / 2; 
              } else {
                sWidth = img.width; 
                sHeight = img.width / canvasAspectRatio; 
                sy = (img.height - sHeight) / 2; 
              }

              canvas.width = targetWidth;
              canvas.height = targetHeight;
              ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
            }
            
            resolve(canvas.toDataURL('image/png').split(',')[1]);
          };
          img.onerror = function() {
            reject(new Error("COULD NOT LOAD IMAGE."));
          };
          img.src = e.target?.result as string;
        };
        reader.onerror = function() {
          reject(new Error("COULD NOT READ FILE."));
        };
        reader.readAsDataURL(file);
      });
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('=��� Logo upload:', {
        name: file.name,
        type: file.type,
        size: file.size,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      });

      // Check file type (reject HEIC/HEIF from iPhones/Android)
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type.toLowerCase())) {
        displayResult(`INVALID IMAGE FORMAT. PLEASE USE PNG, JPG, GIF, OR WEBP. (NOT ${file.type})`, 'error');
        event.target.value = ''; // Reset input
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        displayResult('LOGO IMAGE TOO LARGE. MAXIMUM SIZE IS 10MB. PLEASE RESIZE YOUR IMAGE.', 'error');
        event.target.value = ''; // Reset input
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
        console.log('G�� Logo preview loaded successfully');
      };
      reader.onerror = (error) => {
        console.error('G�� Logo read error:', error);
        displayResult('FAILED TO READ LOGO IMAGE. PLEASE TRY A DIFFERENT FILE.', 'error');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('=��� Cover photo upload:', {
        name: file.name,
        type: file.type,
        size: file.size,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      });

      // Check file type (reject HEIC/HEIF from iPhones/Android)
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type.toLowerCase())) {
        displayResult(`INVALID IMAGE FORMAT. PLEASE USE PNG, JPG, GIF, OR WEBP. (NOT ${file.type})`, 'error');
        event.target.value = ''; // Reset input
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        displayResult('COVER IMAGE TOO LARGE. MAXIMUM SIZE IS 10MB. PLEASE RESIZE YOUR IMAGE.', 'error');
        event.target.value = ''; // Reset input
        return;
      }

      setCoverPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPhotoPreview(e.target?.result as string);
        console.log('G�� Cover photo preview loaded successfully');
      };
      reader.onerror = (error) => {
        console.error('G�� Cover photo read error:', error);
        displayResult('FAILED TO READ COVER IMAGE. PLEASE TRY A DIFFERENT FILE.', 'error');
      };
      reader.readAsDataURL(file);
    }
  };

  const displayResult = (message: string, type: 'success' | 'error' | 'info') => {
    setStatusMessage({ text: message, type });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      displayResult('Contract address copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy: ', err);
      displayResult('Failed to copy address', 'error');
    }
  };

  const showEnhancedSuccess = async (
    tokenName: string, 
    contractAddress: string, 
    symbol: string,
    logoUrl?: string | null,
    description?: string | null,
    deployerAddress?: string,
    poolAddress?: string | null,
    nftTokenId?: string | null,
    privateKey?: string,
    walletAddress?: string
  ) => {
    // Fetch card URL from token_cards table
    let cardUrl = null;
    try {
      const { data: cardData } = await supabase
        .from('token_cards')
        .select('card_url')
        .eq('contract_address', contractAddress)
        .single();
      
      if (cardData) {
        cardUrl = cardData.card_url;
      }
    } catch (err) {
      console.error('Failed to fetch card URL:', err);
    }
    
    const dextoolsUrl = `https://www.dextools.io/app/en/ink/pair-explorer/${contractAddress}`;
    setEnhancedSuccessMessage({ tokenName, contractAddress, dextoolsUrl });
    // Show share modal on top of create modal (don't close create modal yet)
    setShareModalData({ 
      name: tokenName, 
      symbol, 
      contractAddress,
      logo_url: logoUrl,
      description: description,
      deployer_address: deployerAddress,
      created_at: new Date().toISOString(),
      pool_address: poolAddress,
      nft_token_id: nftTokenId,
      card_url: cardUrl,
      privateKey: privateKey,
      walletAddress: walletAddress
    });
    setShowShareModal(true);
    // Clear any existing status message
    setStatusMessage(null);

    // Widget integration: notify parent window if embedded
    try {
      if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: 'mavrk:tokenDeployed', tokenAddress: contractAddress, symbol, name: tokenName },
          '*'
        );
        
        // Track widget deployment
        const queryParams = new URLSearchParams(window.location.search);
        const queryParamsObj: Record<string, string> = {};
        queryParams.forEach((value, key) => {
          queryParamsObj[key] = value;
        });
        
        trackWidgetDeployment(
          contractAddress,
          symbol,
          tokenName,
          document.referrer,
          'iframe',
          queryParamsObj
        ).catch(err => console.error('Failed to track widget deployment:', err));
      }
    } catch {}
  };

  // Helper function to generate and upload token card
  const generateAndUploadTokenCard = async (
    symbol: string,
    logoUrl: string | null,
    description: string | null,
    deployerAddress: string,
    poolAddress: string | null,
    nftTokenId: string | null,
    contractAddress: string
  ): Promise<string | null> => {
    try {
      console.log('?? Generating token card...');
      console.log('Token data:', { symbol, logoUrl, deployerAddress, poolAddress, nftTokenId });
      
      // Fetch ZNS domain for the deployer to display on card (only for Ethereum addresses)
      let creatorZNSName: string | null = null;
      if (deployerAddress && deployerAddress.startsWith('0x') && deployerAddress.length === 42) {
        try {
          const response = await fetch(
            `https://zns.bio/api/resolveAddress?chain=57073&address=${deployerAddress.toLowerCase()}`,
            { signal: AbortSignal.timeout(5000) } // 5 second timeout
          );
          if (response.ok) {
            const data = await response.json();
            if (data.code === 200 && data.primaryDomain) {
              creatorZNSName = `${data.primaryDomain}.ink`;
            }
          }
        } catch (znsError) {
          // Silently fail - ZNS lookup is optional
        }
      }
      
      const cardBlob = await generateTokenCard({
        logo_url: logoUrl,
        symbol,
        description,
        deployer_address: deployerAddress,
        created_at: new Date().toISOString(),
        pool_address: poolAddress,
        nft_token_id: nftTokenId,
        zns_domain: creatorZNSName  // Pass ZNS domain if found
      });

      console.log('? Card generated successfully, size:', cardBlob.size, 'bytes');

      const cardFileName = `${contractAddress}-card.png`; // Currently PNG, but with shimmer effect
      console.log('?? Uploading to bucket:', STORAGE_BUCKETS.CARDS);
      const cardUrl = await uploadBlobToStorage(
        cardBlob,
        STORAGE_BUCKETS.CARDS,
        cardFileName
      );

      if (cardUrl) {
        console.log('? Token card uploaded:', cardUrl);
        
        // Insert card record into token_cards table
        const { error: dbError } = await supabase
          .from('token_cards')
          .upsert({ 
            contract_address: contractAddress,
            card_url: cardUrl,
            deployer_address: deployerAddress,
            updated_at: new Date().toISOString()
          }, { 
            onConflict: 'contract_address',
            ignoreDuplicates: false 
          });

        if (dbError) {
          console.error('? Database insert error:', dbError);
        } else {
          console.log('? Card URL saved to database');
        }
      } else {
        console.error('? Failed to upload card - no URL returned');
      }

      return cardUrl;
    } catch (error) {
      console.error('? Error generating/uploading token card:', error);
      return null;
    }
  };

  // Helper function to create token in Supabase
  const createTokenInSupabase = async (tokenData: {
    name: string;
    symbol: string;
    description: string;
    website: string;
    twitter: string;
    telegram: string;
    discord: string;
    logoBase64: string | null;
    coverPhotoBase64: string | null;
    contractAddress: string;
    deployerAddress: string;
    initialBuyAmount: string;
    dexSelection: 'protofire' | 'inkyswap';
    category: 'Meme' | 'DeFi' | 'Utility' | 'Gaming & Metaverse' | 'SoFi' | 'Creator Coins' | 'AI & Tech' | 'DAO' | 'Experimental/Art' | 'Kraken Girls';
    poolAddress?: string;
    nftTokenId?: string;
    lpProvider?: 'relay' | 'velodrome';
  }) => {
    try {
      displayResult('UPLOADING IMAGES TO SUPABASE STORAGE...', 'info');
      
      // Upload images to Supabase Storage
      let logoUrl = null;
      let coverPhotoUrl = null;
      
      if (tokenData.logoBase64) {
        console.log('=��� Uploading logo image...');
        const logoFileName = `${tokenData.contractAddress}-logo.png`;
        logoUrl = await uploadBase64ImageToStorage(
          tokenData.logoBase64,
          STORAGE_BUCKETS.LOGOS,
          logoFileName
        );
        if (!logoUrl) {
          throw new Error('Failed to upload logo image');
        }
        console.log('G�� Logo uploaded:', logoUrl);
      }
      
      if (tokenData.coverPhotoBase64) {
        console.log('=��� Uploading cover photo...');
        const coverFileName = `${tokenData.contractAddress}-cover.png`;
        coverPhotoUrl = await uploadBase64ImageToStorage(
          tokenData.coverPhotoBase64,
          STORAGE_BUCKETS.COVERS,
          coverFileName
        );
        if (!coverPhotoUrl) {
          throw new Error('Failed to upload cover photo');
        }
        console.log('G�� Cover photo uploaded:', coverPhotoUrl);
      }

      console.log('=��+ Saving token record to database...');
      displayResult('SAVING TOKEN METADATA TO DATABASE...', 'info');

      // Create token record in Supabase
      const tokenRecord = {
        name: tokenData.name,
        symbol: tokenData.symbol,
        description: tokenData.description || undefined,
        website: tokenData.website || undefined,
        twitter: tokenData.twitter || undefined,
        telegram: tokenData.telegram || undefined,
        discord: tokenData.discord || undefined,
        logo_url: logoUrl || undefined,
        cover_photo_url: coverPhotoUrl || undefined,
        contract_address: tokenData.contractAddress,
        network: 'ink' as const,
        pool_address: tokenData.poolAddress || undefined,
        nft_token_id: tokenData.nftTokenId || undefined,
        deployer_address: tokenData.deployerAddress,
        initial_buy_amount: tokenData.initialBuyAmount,
        dex: tokenData.dexSelection === 'protofire' ? 'Protofire' : 'InkySwap',
        category: tokenData.category,
      };

      const result = await createToken(tokenRecord, tokenData.lpProvider);
      
      displayResult('TOKEN METADATA SAVED SUCCESSFULLY!', 'success');
      return { ...result, logoUrl, coverPhotoUrl };
    } catch (error) {
      console.error('Error creating token in Supabase:', error);
      displayResult(`FAILED TO SAVE TOKEN METADATA: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
      throw error;
    }
  };

  const handleDeployClick = () => {
    console.log('?? Deploy button clicked!');
    console.log('Wallet connected:', isConnected);
    console.log('Form data:', formData);
    console.log('Deployment mode:', deploymentMode);
    
    // If no wallet connected, use platform wallet (hidden mode)
    if (!isConnected) {
      console.log('No wallet connected - calling handlePlatformDeploy');
      handlePlatformDeploy();
    } else {
      console.log('Wallet connected - opening first buy modal');
      // Reset buy selection when opening modal
      setSelectedBuyPercentage(null);
      setFirstBuyEth(0.000001);
      setShowFirstBuyModal(true);
    }
  };

  const handleModalDeploy = async (useCustom: boolean, customEth?: number) => {
    console.log('?? Modal deploy called!');
    console.log('Use custom buy:', useCustom);
    console.log('Custom ETH:', customEth);
    console.log('Deployment mode:', deploymentMode);
    
    setShowFirstBuyModal(false);
    
    if (useCustom && customEth) {
      // User wants to buy with custom amount
      const ethAmount = customEth;
      console.log('Setting initial buy amount:', ethAmount);
      setFormData((prev) => ({ ...prev, initialBuyAmount: ethAmount.toFixed(6) }));
    
      // Call the appropriate deployment function based on mode
      if (deploymentMode === 'gasless') {
        console.log('Calling handleGaslessDeploy with', ethAmount);
        setTimeout(() => handleGaslessDeploy(ethAmount), 0);
      } else {
        console.log('Calling handleSelfFundedDeploy with', ethAmount);
        setTimeout(() => handleSelfFundedDeploy(ethAmount), 0);
      }
    } else {
      // User clicked "No" - deploy without any initial buy
      console.log('Deploying without initial buy');
      setFormData((prev) => ({ ...prev, initialBuyAmount: '0' }));
      
      // Call the appropriate deployment function with 0 ETH
      if (deploymentMode === 'gasless') {
        console.log('Calling handleGaslessDeploy with 0');
        setTimeout(() => handleGaslessDeploy(0), 0);
      } else {
        console.log('Calling handleSelfFundedDeploy with 0');
        setTimeout(() => handleSelfFundedDeploy(0), 0);
      }
    }
  };

  /* Platform Wallet Deployment - No user wallet required! */

  // Platform wallet deployment function (frictionless!)
  const handlePlatformDeploy = async () => {
    if (!formData.name || !formData.symbol) {
      displayResult('PLEASE FILL IN ALL REQUIRED TOKEN INFORMATION (NAME AND TICKER).', 'error');
      return;
    }

    if (!logoFile) {
      displayResult('LOGO IMAGE IS REQUIRED. PLEASE UPLOAD A LOGO TO CONTINUE.', 'error');
      return;
    }

    // Validate against blacklist
    const validation = validateTokenContent(
      formData.name, 
      formData.symbol,
      formData.description,
      formData.website,
      formData.twitter,
      formData.telegram,
      formData.discord
    );
    if (!validation.valid) {
      displayResult(`${validation.error}`, 'error');
      return;
    }

    // Show CAPTCHA if not verified yet
    if (!captchaVerified) {
      setShowCaptcha(true);
      displayResult('=��� PLEASE COMPLETE THE CAPTCHA TO CONTINUE', 'info');
      return;
    }

    // If CAPTCHA is verified, proceed with deployment
    proceedWithPlatformDeployment();
  };

  // Actual platform deployment logic (after CAPTCHA verification)
  // Uses temporary wallet - user gets private key to import later
  const proceedWithPlatformDeployment = async (isCaptchaVerified: boolean = captchaVerified) => {
    console.log('=��� PROCEEDING WITH TEMPORARY WALLET DEPLOYMENT - captchaVerified:', isCaptchaVerified);
    setIsPlatformDeploying(true);
      displayResult('GENERATING TEMPORARY WALLET & DEPLOYING TOKEN...', 'info');

    try {
      let logoBase64 = null;
      let coverPhotoBase64 = null;
      
      if (logoFile) {
        logoBase64 = await resizeImage(logoFile, 1024); // High quality for token card
      }
      if (coverPhotoFile) {
        coverPhotoBase64 = await resizeImage(coverPhotoFile, 1360);
      }

      displayResult('CREATING YOUR CREATOR WALLET...', 'info');
      displayResult('DEPLOYING VIA GELATO (GASLESS)...', 'info');

      // Get NPM address based on DEX selection
      const npmAddress = formData.dexSelection === 'protofire' ? NPM_ADDRESSES.PROTOFIRE : NPM_ADDRESSES.INKYSWAP;
      
      // Deploy using temporary wallet (generates wallet, deploys, returns private key)
      const result = await deployWithTemporaryWallet({
        name: formData.name,
        symbol: formData.symbol,
        npmAddress: npmAddress,
      });

      if (!result.success) {
        throw new Error(result.error || 'Deployment failed');
      }

      const deployedTokenAddress = result.tokenAddress!;
      const poolAddress = result.poolAddress;
      const nftTokenId = result.nftTokenId;
      const privateKey = result.privateKey!;
      const walletAddress = result.walletAddress!;

      displayResult('DEPLOYMENT SUCCESSFUL! SAVING METADATA...', 'success');

      // Save token metadata to Supabase with temp wallet as deployer
      try {
        const supabaseResult = await createTokenInSupabase({
          name: formData.name,
          symbol: formData.symbol,
          description: formData.description,
          website: formData.website,
          twitter: formData.twitter,
          telegram: formData.telegram,
          discord: formData.discord,
          logoBase64: logoBase64,
          coverPhotoBase64: coverPhotoBase64,
          contractAddress: deployedTokenAddress,
          deployerAddress: walletAddress, // Temp wallet address (user's creator wallet)
          initialBuyAmount: '0', // No initial buy
          dexSelection: formData.dexSelection,
          category: formData.category,
          poolAddress: poolAddress,
          nftTokenId: nftTokenId,
          lpProvider: lpProvider,
        });
        
        // Generate and upload token card
        console.log('Attempting to generate token card, logoUrl:', supabaseResult?.logoUrl);
        if (supabaseResult?.logoUrl) {
          setDeploymentProgress('Generating Creator Token Card...');
          const cardUrl = await generateAndUploadTokenCard(
            formData.symbol,
            supabaseResult.logoUrl,
            formData.description,
            walletAddress,
            poolAddress || null,
            nftTokenId || null,
            deployedTokenAddress
          );
          console.log('Card generation result:', cardUrl);
        } else {
          console.log('Skipping card generation - no logo URL');
        }
        
        displayResult('METADATA SAVED TO SUPABASE!', 'success');
      } catch (supabaseError) {
        console.error("SUPABASE METADATA STORAGE FAILED:", supabaseError);
        displayResult('TOKEN DEPLOYED BUT METADATA SAVING FAILED. PLEASE CONTACT SUPPORT.', 'info');
      }

      // Fetch the complete token data from Supabase to show in share modal
      try {
        const { data: tokenData } = await supabase
          .from('tokens')
          .select('*')
          .eq('contract_address', deployedTokenAddress)
          .single();
        
        if (tokenData) {
          await showEnhancedSuccess(
            formData.name,
            deployedTokenAddress,
            formData.symbol,
            tokenData.logo_url,
            formData.description,
            walletAddress,
            poolAddress,
            nftTokenId,
            privateKey,  // Pass private key
            walletAddress  // Pass wallet address
          );
        } else {
          await showEnhancedSuccess(
            formData.name,
            deployedTokenAddress,
            formData.symbol,
            null,
            null,
            walletAddress,
            poolAddress,
            nftTokenId,
            privateKey,  // Pass private key
            walletAddress  // Pass wallet address
          );
        }
      } catch (err) {
        console.error('Failed to fetch token data:', err);
        await showEnhancedSuccess(
          formData.name,
          deployedTokenAddress,
          formData.symbol,
          null,
          null,
          walletAddress,
          poolAddress,
          nftTokenId,
          privateKey,  // Pass private key
          walletAddress  // Pass wallet address
        );
      }
      
      // Don't show separate private key modal - it's now in the share modal
      // setDeploymentPrivateKey(privateKey);
      // setDeploymentWalletAddress(walletAddress);
      // setDeploymentTokenAddress(deployedTokenAddress);
      // setShowPrivateKeyModal(true);
      
      // Reset CAPTCHA state
      setCaptchaVerified(false);
      setShowCaptcha(false);
      
    } catch (error: any) {
      console.error("G�� TEMPORARY WALLET DEPLOYMENT FAILED:", error);
      displayResult(`DEPLOYMENT FAILED: ${error.message}`, 'error');
      // Reset CAPTCHA state for retry
      setCaptchaVerified(false);
      setShowCaptcha(false);
    } finally {
      setIsPlatformDeploying(false);
    }
  };

  // Handle private key modal close (after user acknowledges)
  const handlePrivateKeyModalClose = () => {
    setShowPrivateKeyModal(false);
    
    // Reset form after modal closes
    setFormData({
      name: '',
      symbol: '',
      description: '',
      logo: '',
      coverPhoto: '',
      website: '',
      twitter: '',
      telegram: '',
      discord: '',
      initialBuyAmount: '0.000001',
      dexSelection: 'protofire',
      category: 'Meme',
    });
    setLogoFile(null);
    setCoverPhotoFile(null);
    setLogoPreview('');
    setCoverPhotoPreview('');
    
    // Clear deployment data
    setDeploymentPrivateKey('');
    setDeploymentWalletAddress('');
    setDeploymentTokenAddress('');
  };

  // Gasless deployment function using Gelato
  const handleGaslessDeploy = async (ethAmount?: number) => {
    console.log('? handleGaslessDeploy called with ethAmount:', ethAmount);
    console.log('Is connected:', isConnected);
    console.log('Address:', address);
    console.log('Wallet client:', walletClient);
    
    if (!isConnected || !address) {
      displayResult('PLEASE CONNECT YOUR WALLET FIRST.', 'error');
      return;
    }

    if (!walletClient) {
      displayResult('WALLET CLIENT NOT AVAILABLE. PLEASE REFRESH AND TRY AGAIN.', 'error');
      return;
    }

    if (!formData.name || !formData.symbol) {
      displayResult('PLEASE FILL IN ALL REQUIRED TOKEN INFORMATION (NAME AND TICKER).', 'error');
      return;
    }

    if (!logoFile) {
      displayResult('LOGO IMAGE IS REQUIRED. PLEASE UPLOAD A LOGO TO CONTINUE.', 'error');
      return;
    }

    // Validate against blacklist (name, symbol, and all optional fields)
    const validation = validateTokenContent(
      formData.name, 
      formData.symbol,
      formData.description,
      formData.website,
      formData.twitter,
      formData.telegram,
      formData.discord
    );
    if (!validation.valid) {
      displayResult(`${validation.error}`, 'error');
      return;
    }

    setIsGaslessDeploying(true);
      displayResult('INITIATING GASLESS TOKEN DEPLOYMENT VIA GELATO...', 'info');

    try {
      // Use wagmi's wallet client to create an ethers provider
      const provider = new ethers.BrowserProvider(walletClient);
      const network = await provider.getNetwork();
      
      if (network.chainId !== BigInt(57073)) {
        displayResult(`PLEASE SWITCH YOUR WALLET TO INK (CHAIN ID: 57073) BEFORE DEPLOYING.`, 'error');
        setIsGaslessDeploying(false);
        return;
      }

      let logoBase64 = null;
      let coverPhotoBase64 = null;
      
      if (logoFile) {
        logoBase64 = await resizeImage(logoFile, 1024); // High quality for token card
      }
      if (coverPhotoFile) {
        coverPhotoBase64 = await resizeImage(coverPhotoFile, 1360);
      }

      displayResult('SENDING GASLESS TRANSACTION TO GELATO...', 'info');
      setDeploymentProgress('Sending gas sponsorship request...');

      // Get NPM address based on DEX selection
      const npmAddress = formData.dexSelection === 'protofire' ? NPM_ADDRESSES.PROTOFIRE : NPM_ADDRESSES.INKYSWAP;

      // Deploy token using Gelato gasless transaction
      const gaslessResult = await deployTokenGasless({
        name: formData.name,
        symbol: formData.symbol,
        npmAddress: npmAddress,
        userAddress: address,
        provider: provider,
        lpProvider: lpProvider,
      });

      if (!gaslessResult.success) {
        throw new Error(gaslessResult.error || 'Gasless deployment failed');
      }

      displayResult('WAITING FOR GELATO TO EXECUTE TRANSACTION...', 'info');
      setDeploymentProgress('Waiting for transaction execution...');

      // Wait for task completion
      const taskResult = await waitForTaskCompletion(
        gaslessResult.taskId,
        (status) => {
          displayResult(`GELATO STATUS: ${status}...`, 'info');
        }
      );

      if (!taskResult.success) {
        throw new Error(taskResult.error || 'Task execution failed');
      }

      displayResult('GASLESS DEPLOYMENT SUCCESSFUL! PARSING TRANSACTION...', 'success');
      setDeploymentProgress('Fetching receipt...');

      // Get transaction receipt with RPC failover to extract token address
      displayResult('FETCHING TRANSACTION RECEIPT WITH BACKUP RPCS...', 'info');
      const receipt = await getTransactionReceiptWithFailover(taskResult.transactionHash!);
      
      if (!receipt || !receipt.status) {
        throw new Error('Transaction failed, was reverted, or receipt could not be fetched from any RPC');
      }
      
      displayResult('TRANSACTION RECEIPT FETCHED SUCCESSFULLY!', 'success');
      setDeploymentProgress('Receipt found!\nParsing transaction data...');

      // Parse token address from logs using the correct factory ABI based on LP provider
      const factoryABI = getFactoryABI(lpProvider);
      const factoryAddress = getFactoryAddress(lpProvider);
      const factoryContract = new ethers.Contract(factoryAddress, factoryABI, provider);
      let deployedTokenAddress = null;
      let poolAddress = null;
      let nftTokenId = null;

      console.log(`[CreateToken] Parsing logs with ${lpProvider} factory:`, { factoryAddress, factoryABI });

      for (const log of receipt.logs) {
        try {
          const parsedLog = factoryContract.interface.parseLog(log);
          if (parsedLog) {
            console.log(`[CreateToken] Parsed event: ${parsedLog.name}`, parsedLog.args);
            
            if (parsedLog.name === 'TokenDeployed') {
              deployedTokenAddress = parsedLog.args.tokenAddress;
              console.log('? Token Address:', deployedTokenAddress);
              setDeploymentProgress('Token deployed!');
            }
               if (parsedLog.name === 'PoolInitialized') {
                 poolAddress = parsedLog.args.pool;
                 console.log('? Pool Address (PoolInitialized):', poolAddress);
                 setDeploymentProgress('Pool initialized!');
               }
               if (parsedLog.name === 'PoolCreated') {
                 poolAddress = parsedLog.args.pool;
                 console.log('? Pool Address (PoolCreated):', poolAddress);
                 setDeploymentProgress('Pool created!');
               }
               if (parsedLog.name === 'Mint') {
                 // The Mint event doesn't directly contain pool address, but we can log it for debugging
                 console.log('? Mint event found:', parsedLog.args);
               }
               if (parsedLog.name === 'Transfer') {
                 // Look for Transfer event from factory to pool
                 // The factory transfers tokens to the pool during deployment
                 if (parsedLog.args.from && parsedLog.args.from.toLowerCase() === factoryAddress.toLowerCase()) {
                   poolAddress = parsedLog.args.to;
                   console.log('? Pool Address (from Transfer event):', poolAddress);
                 }
               }
            if (parsedLog.name === 'LiquidityMinted') {
              nftTokenId = parsedLog.args.tokenId.toString();
              console.log('? NFT Token ID (from LiquidityMinted):', nftTokenId);
              setDeploymentProgress('Minting NFT...');
            }
            if (parsedLog.name === 'NFTLocked') {
              // Also check NFTLocked in case it's there
              if (!nftTokenId) {
                nftTokenId = parsedLog.args.tokenId.toString();
                console.log('? NFT Token ID (from NFTLocked):', nftTokenId);
                setDeploymentProgress('NFT locked!');
              }
            }
          }
        } catch (e) {
          // Log might not be from our factory contract
          console.log('?? Could not parse log (might be from another contract)');
        }
      }

      console.log('?? Parsed deployment data:', { deployedTokenAddress, poolAddress, nftTokenId });

      if (!deployedTokenAddress) {
        throw new Error("COULD NOT DETERMINE DEPLOYED TOKEN ADDRESS FROM TRANSACTION RECEIPT.");
      }

      // Pool address is extracted from Transfer event above (factory -> pool token transfer)

      // Handle initial buy if specified (same as traditional deployment)
      const initialBuyAmount = ethAmount !== undefined ? ethAmount : (parseFloat(formData.initialBuyAmount) || 0.000001);
      
      if (initialBuyAmount > 0) {
        displayResult('EXECUTING INITIAL BUY TO SET MARKET CAP...', 'info');
        try {
          const signer = await provider.getSigner();
          // Get correct router based on DEX selection
          const swapRouterAddress = formData.dexSelection === 'inkyswap' 
            ? SWAP_ROUTER_ADDRESSES.INKYSWAP 
            : SWAP_ROUTER_ADDRESSES.PROTOFIRE;
          const routerContract = new ethers.Contract(swapRouterAddress, ROUTER_ABI, signer);
          const buyAmountWei = ethers.parseEther(initialBuyAmount.toString());
          
          const swapTx = await routerContract.exactInputSingle({
            tokenIn: WETH_ADDRESS,
            tokenOut: deployedTokenAddress,
            fee: 10000,
            recipient: address,
            amountIn: buyAmountWei,
            amountOutMinimum: 0,
            sqrtPriceLimitX96: 0
          }, {
            gasLimit: 800000,
            value: buyAmountWei
          });
          await swapTx.wait();
          displayResult('INITIAL BUY COMPLETED! YOU ARE NOW THE FIRST BUYER OF YOUR TOKEN.', 'success');
        } catch (buyError) {
          console.error("INITIAL BUY FAILED:", buyError);
          displayResult('TOKEN DEPLOYED BUT INITIAL BUY FAILED. YOU CAN MANUALLY BUY YOUR TOKEN LATER.', 'info');
        }
      }

      // Save token metadata to Supabase
      try {
        const supabaseResult = await createTokenInSupabase({
          name: formData.name,
          symbol: formData.symbol,
          description: formData.description,
          website: formData.website,
          twitter: formData.twitter,
          telegram: formData.telegram,
          discord: formData.discord,
          logoBase64: logoBase64,
          coverPhotoBase64: coverPhotoBase64,
          contractAddress: deployedTokenAddress,
          deployerAddress: address,
          initialBuyAmount: formData.initialBuyAmount,
          dexSelection: formData.dexSelection,
          category: formData.category,
          poolAddress: poolAddress,
          nftTokenId: nftTokenId,
          lpProvider: lpProvider,
        });

        // Generate and upload token card
        setDeploymentProgress('Generating Creator Token Card...');
        if (supabaseResult?.logoUrl) {
          await generateAndUploadTokenCard(
            formData.symbol,
            supabaseResult.logoUrl,
            formData.description,
            address,
            poolAddress || null,
            nftTokenId || null,
            deployedTokenAddress
          );
        }
        setDeploymentProgress('Upload successful!');
      } catch (supabaseError) {
        console.error("SUPABASE METADATA STORAGE FAILED:", supabaseError);
        displayResult('TOKEN DEPLOYED BUT METADATA SAVING FAILED. PLEASE CONTACT SUPPORT.', 'info');
      }

      // Fetch the complete token data from Supabase
      try {
        const { data: tokenData } = await supabase
          .from('tokens')
          .select('*')
          .eq('contract_address', deployedTokenAddress)
          .single();
        
        if (tokenData) {
          await showEnhancedSuccess(
            formData.name,
            deployedTokenAddress,
            formData.symbol,
            tokenData.logo_url,
            formData.description,
            address,
            poolAddress,
            nftTokenId
          );
        } else {
          await showEnhancedSuccess(formData.name, deployedTokenAddress, formData.symbol);
        }
      } catch (err) {
        console.error('Failed to fetch token data:', err);
        await showEnhancedSuccess(formData.name, deployedTokenAddress, formData.symbol);
      }
      
      // Reset form
      setFormData({
        name: '',
        symbol: '',
        description: '',
        logo: '',
        coverPhoto: '',
        website: '',
        twitter: '',
        telegram: '',
        discord: '',
        initialBuyAmount: '0.000001',
        dexSelection: 'protofire',
        category: 'Meme',
      });
      setLogoFile(null);
      setCoverPhotoFile(null);
      setLogoPreview('');
      setCoverPhotoPreview('');
      
    } catch (error: any) {
      console.error("G�� GASLESS DEPLOYMENT FAILED:", error);
      let errorMessage = error.message || 'Unknown error';
      
      // Simplify common error messages
      if (errorMessage.includes("rejected") || errorMessage.includes("User denied") || errorMessage.includes("user rejected")) {
        displayResult('User rejected the deployment.', 'error');
      } else if (errorMessage.includes("insufficient")) {
        displayResult('Insufficient gas tank balance or invalid API key.', 'error');
      } else {
        displayResult(`Deployment failed: ${errorMessage.substring(0, 100)}`, 'error');
      }
    } finally {
      setIsGaslessDeploying(false);
    }
  };

  // Self-funded deployment function (user pays gas)
  const handleSelfFundedDeploy = async (ethAmount?: number) => {
    console.log('?? handleSelfFundedDeploy called with ethAmount:', ethAmount);
    console.log('Is connected:', isConnected);
    console.log('Address:', address);
    console.log('Wallet client:', walletClient);
    
    if (!isConnected || !address) {
      displayResult('PLEASE CONNECT YOUR WALLET FIRST.', 'error');
      return;
    }

    if (!walletClient) {
      displayResult('WALLET CLIENT NOT AVAILABLE. PLEASE REFRESH AND TRY AGAIN.', 'error');
      return;
    }

    if (!formData.name || !formData.symbol) {
      displayResult('PLEASE FILL IN ALL REQUIRED TOKEN INFORMATION (NAME AND TICKER).', 'error');
      return;
    }

    if (!logoFile) {
      displayResult('LOGO IMAGE IS REQUIRED. PLEASE UPLOAD A LOGO TO CONTINUE.', 'error');
      return;
    }

    // Validate against blacklist
    const validation = validateTokenContent(
      formData.name, 
      formData.symbol,
      formData.description,
      formData.website,
      formData.twitter,
      formData.telegram,
      formData.discord
    );
    if (!validation.valid) {
      displayResult(`${validation.error}`, 'error');
      return;
    }

    setIsSelfFundedDeploying(true);
    setDeploymentProgress('Preparing transaction...');
    displayResult('INITIATING SELF-FUNDED TOKEN DEPLOYMENT...', 'info');

    try {
      // Use wagmi's wallet client to create an ethers provider
      const provider = new ethers.BrowserProvider(walletClient);
      const network = await provider.getNetwork();
      
      if (network.chainId !== BigInt(57073)) {
        displayResult(`PLEASE SWITCH YOUR WALLET TO INK (CHAIN ID: 57073) BEFORE DEPLOYING.`, 'error');
        setIsSelfFundedDeploying(false);
        return;
      }

      let logoBase64 = null;
      let coverPhotoBase64 = null;
      
      if (logoFile) {
        logoBase64 = await resizeImage(logoFile, 1024); // High quality for token card
      }
      if (coverPhotoFile) {
        coverPhotoBase64 = await resizeImage(coverPhotoFile, 1360);
      }

      displayResult('SENDING TRANSACTION TO NETWORK...', 'info');
      setDeploymentProgress('Sending transaction to network...');

      const signer = await provider.getSigner();
      const factoryContract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, signer);

      // Get NPM address based on DEX selection
      const npmAddress = formData.dexSelection === 'protofire' ? NPM_ADDRESSES.PROTOFIRE : NPM_ADDRESSES.INKYSWAP;

      // Deploy token with custom gas price (0.0005 GWEI = 500,000 wei)
      const tx = await factoryContract.newMavrkToken(
        formData.name,
        formData.symbol,
        npmAddress
      );

      displayResult('WAITING FOR TRANSACTION CONFIRMATION...', 'info');
      setDeploymentProgress('Waiting for transaction confirmation...');
      const receipt = await tx.wait();
      
      if (!receipt || !receipt.status) {
        throw new Error('Transaction failed or was reverted');
      }

      displayResult('DEPLOYMENT SUCCESSFUL! PARSING TRANSACTION...', 'success');
      setDeploymentProgress('Receipt found!\nParsing transaction data...');

      // Parse token address from logs
      let deployedTokenAddress = null;
      let poolAddress = null;
      let nftTokenId = null;

      for (const log of receipt.logs) {
        try {
          const parsedLog = factoryContract.interface.parseLog(log);
          if (parsedLog) {
            if (parsedLog.name === 'TokenDeployed') {
              deployedTokenAddress = parsedLog.args.tokenAddress;
              setDeploymentProgress('Token deployed!');
            }
            if (parsedLog.name === 'PoolInitialized') {
              poolAddress = parsedLog.args.pool;
              setDeploymentProgress('Pool initialized!');
            }
            if (parsedLog.name === 'NFTLocked') {
              nftTokenId = parsedLog.args.tokenId.toString();
              setDeploymentProgress('NFT locked!');
            }
          }
        } catch (e) {
          // Continue parsing other logs
        }
      }

      if (!deployedTokenAddress) {
        throw new Error("COULD NOT DETERMINE DEPLOYED TOKEN ADDRESS FROM TRANSACTION RECEIPT.");
      }

      // Handle initial buy if specified
      const initialBuyAmount = ethAmount !== undefined ? ethAmount : (parseFloat(formData.initialBuyAmount) || 0.000001);
      
      if (initialBuyAmount > 0) {
        displayResult('EXECUTING INITIAL BUY TO SET MARKET CAP...', 'info');
        try {
          // Use private RPC for reliable swap execution
          const privateRpc = config.ink.primaryRpcUrl;
          const privateProvider = new ethers.JsonRpcProvider(privateRpc);
          const networkSigner = await provider.getSigner();
          const address = await networkSigner.getAddress();
          
          // Get correct router based on DEX selection
          const swapRouterAddress = formData.dexSelection === 'inkyswap' 
            ? SWAP_ROUTER_ADDRESSES.INKYSWAP 
            : SWAP_ROUTER_ADDRESSES.PROTOFIRE;
          const routerContract = new ethers.Contract(swapRouterAddress, ROUTER_ABI, networkSigner);
          const buyAmountWei = ethers.parseEther(initialBuyAmount.toString());
          
          const swapTx = await routerContract.exactInputSingle({
            tokenIn: WETH_ADDRESS,
            tokenOut: deployedTokenAddress,
            fee: 10000,
            recipient: address,
            amountIn: buyAmountWei,
            amountOutMinimum: 0,
            sqrtPriceLimitX96: 0
          }, {
            gasLimit: 800000,
            value: buyAmountWei
          });
          await swapTx.wait();
          displayResult('INITIAL BUY COMPLETED! YOU ARE NOW THE FIRST BUYER OF YOUR TOKEN.', 'success');
        } catch (buyError) {
          console.error("INITIAL BUY FAILED:", buyError);
          displayResult('TOKEN DEPLOYED BUT INITIAL BUY FAILED. YOU CAN MANUALLY BUY YOUR TOKEN LATER.', 'info');
        }
      }

      // Save token metadata to Supabase
      try {
        const supabaseResult = await createTokenInSupabase({
          name: formData.name,
          symbol: formData.symbol,
          description: formData.description,
          website: formData.website,
          twitter: formData.twitter,
          telegram: formData.telegram,
          discord: formData.discord,
          logoBase64: logoBase64,
          coverPhotoBase64: coverPhotoBase64,
          contractAddress: deployedTokenAddress,
          deployerAddress: address,
          initialBuyAmount: formData.initialBuyAmount,
          dexSelection: formData.dexSelection,
          category: formData.category,
          poolAddress: poolAddress,
          nftTokenId: nftTokenId,
          lpProvider: lpProvider,
        });

        // Generate and upload token card
        setDeploymentProgress('Generating Creator Token Card...');
        if (supabaseResult?.logoUrl) {
          await generateAndUploadTokenCard(
            formData.symbol,
            supabaseResult.logoUrl,
            formData.description,
            address,
            poolAddress || null,
            nftTokenId || null,
            deployedTokenAddress
          );
        }
        setDeploymentProgress('Upload successful!');
      } catch (supabaseError) {
        console.error("SUPABASE METADATA STORAGE FAILED:", supabaseError);
        displayResult('TOKEN DEPLOYED BUT METADATA SAVING FAILED. PLEASE CONTACT SUPPORT.', 'info');
      }

      // Fetch the complete token data from Supabase
      try {
        const { data: tokenData } = await supabase
          .from('tokens')
          .select('*')
          .eq('contract_address', deployedTokenAddress)
          .single();
        
        if (tokenData) {
          await showEnhancedSuccess(
            formData.name,
            deployedTokenAddress,
            formData.symbol,
            tokenData.logo_url,
            formData.description,
            address,
            poolAddress,
            nftTokenId
          );
        } else {
          await showEnhancedSuccess(formData.name, deployedTokenAddress, formData.symbol);
        }
      } catch (err) {
        console.error('Failed to fetch token data:', err);
        await showEnhancedSuccess(formData.name, deployedTokenAddress, formData.symbol);
      }
      
      // Reset form
      setFormData({
        name: '',
        symbol: '',
        description: '',
        logo: '',
        coverPhoto: '',
        website: '',
        twitter: '',
        telegram: '',
        discord: '',
        initialBuyAmount: '0.000001',
        dexSelection: 'protofire',
        category: 'Meme',
      });
      setLogoFile(null);
      setCoverPhotoFile(null);
      setLogoPreview('');
      setCoverPhotoPreview('');
      
    } catch (error: any) {
      console.error("G�� SELF-FUNDED DEPLOYMENT FAILED:", error);
      let errorMessage = error.message || 'Unknown error';
      
      // Simplify common error messages
      if (errorMessage.includes("rejected") || errorMessage.includes("User denied") || errorMessage.includes("user rejected")) {
        displayResult('User rejected the deployment.', 'error');
      } else if (errorMessage.includes("insufficient")) {
        displayResult('Insufficient ETH balance to pay for gas.', 'error');
      } else {
        displayResult(`Deployment failed: ${errorMessage.substring(0, 100)}`, 'error');
      }
    } finally {
      setIsSelfFundedDeploying(false);
    }
  };

  // If the component is not visible, don't render anything
  if (!isVisible) return null;

  // The form content that will be rendered in either modal or page mode
  const formContent = (
    <CreateContainer $asPage={asPage}>
          {(isGaslessDeploying || isSelfFundedDeploying) && (
            <SpinnerOverlay>
              <SpinnerBox>
                <Hourglass />
                <SpinnerText>
                  {isGaslessDeploying 
                    ? 'Deploying your token...\nPlease sign the transaction in your wallet to authorize deployment.' 
                    : 'Deploying your token...\nPlease confirm the transaction in your wallet.'}
                </SpinnerText>
                {deploymentProgress && (
                  <ProgressMessage>
                    {deploymentProgress}
                  </ProgressMessage>
                )}
              </SpinnerBox>
            </SpinnerOverlay>
          )}
          <FormContainer>
            {/* Token Preview Panel */}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Label>Token Preview</Label>
            </div>
            {showPreview && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
                {/* Token header mock with banner (3:1 width:height), up to 1500x500 */}
                <div style={{ background: '#000', border: '1px solid #444', borderRadius: 10, overflow: 'hidden', width: '100%', maxWidth: 1500, margin: '0 auto' }}>
                  <div style={{ width: '100%', aspectRatio: '3 / 1', maxHeight: 500, position: 'relative', background: '#000' }}>
                    {coverPhotoPreview ? <img src={coverPhotoPreview} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                    <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 100, height: 100, borderRadius: '50%', overflow: 'hidden', background: '#000', border: '1px solid #333' }}>
                        {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                      </div>
                      <div style={{ background: '#000', padding: '6px 10px', borderRadius: 6, fontSize: 12, border: '1px solid #333' }}>
                        {(formData.name || 'Token')} {(formData.symbol ? `(${formData.symbol})` : '(SYMB)')}
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: 10, display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      style={{ flex: 1, background: 'var(--mv-button-bg, #000000)', border: '1px solid #333333', color: '#ffffff', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
                    >
                      Upload Logo
                    </button>
                    <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
                    <button
                      type="button"
                      onClick={() => coverPhotoInputRef.current?.click()}
                      style={{ flex: 1, background: 'var(--mv-button-bg, #000000)', border: '1px solid #333333', color: '#ffffff', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
                    >
                      Upload Banner
                    </button>
                    <input ref={coverPhotoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleCoverPhotoUpload} style={{ display: 'none' }} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Desktop: stacked rows - 1) Name/Symbol/Category, 2) DEX, 3) Description, 4) Socials */}
            {compact ? (
              <>
                <HorizontalFormRow style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                  <FormGroup $compact={compact}>
                    <Label $compact={compact} htmlFor="token-name" className="required">Name</Label>
                    <Input $compact={compact}
                      type="text" 
                      id="token-name" 
                      placeholder="Ex: Super Ink" 
                      maxLength={50}
                      value={formData.name}
                      onChange={handleInputChange}
                      name="name"
                    />
                  </FormGroup>
                  
                  <FormGroup $compact={compact}>
                    <Label $compact={compact} htmlFor="token-ticker" className="required">Symbol</Label>
                    <Input $compact={compact}
                      type="text" 
                      id="token-ticker" 
                      placeholder="Ex: SINK" 
                      maxLength={10}
                      value={formData.symbol}
                      onChange={handleSymbolChange}
                    />
                  </FormGroup>
                  
                  <FormGroup $compact={compact}>
                    <Label $compact={compact} htmlFor="token-category" className="required">Category</Label>
                    <CollapsibleSection>
                      <CollapsibleHeader onClick={() => setIsCategorySectionOpen(!isCategorySectionOpen)}>
                        <span>{formData.category}</span>
                        <CollapsibleIcon isOpen={isCategorySectionOpen}>▼</CollapsibleIcon>
                      </CollapsibleHeader>
                      <CollapsibleContent isOpen={isCategorySectionOpen}>
                        <CollapsibleInner>
                          <CategoryButtonGrid>
                            {(['Meme', 'DeFi', 'Utility', 'Gaming & Metaverse', 'SoFi', 'Creator Coins', 'AI & Tech', 'DAO', 'Experimental/Art', 'Kraken Girls'] as const).map((category) => (
                              <CategoryButton
                                key={category}
                                selected={formData.category === category}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, category }));
                                }}
                                type="button"
                              >
                                {category}
                              </CategoryButton>
                            ))}
                          </CategoryButtonGrid>
                        </CollapsibleInner>
                      </CollapsibleContent>
                    </CollapsibleSection>
                  </FormGroup>
                </HorizontalFormRow>
                {selectedChain === 'ink' && (
                  <FormGroup $compact={compact}>
                    <Label $compact={compact} htmlFor="dex-selection" className="required">DEX DESTINATION</Label>
                    <VerticalOptionsContainer>
                      {(allowedDex === 'both' || allowedDex === 'protofire') && (
                        <ModeOption $compact={compact}
                          selected={formData.dexSelection === 'protofire'}
                          onClick={() => setFormData(prev => ({ ...prev, dexSelection: 'protofire' }))}
                        >
                          <ModeTitle>
                            Protofire
                          </ModeTitle>
                        </ModeOption>
                      )}
                      {(allowedDex === 'both' || allowedDex === 'inkyswap') && (
                        <ModeOption $compact={compact}
                          selected={formData.dexSelection === 'inkyswap'}
                          onClick={() => setFormData(prev => ({ ...prev, dexSelection: 'inkyswap' }))}
                        >
                          <ModeTitle>InkySwap</ModeTitle>
                        </ModeOption>
                      )}
                    </VerticalOptionsContainer>
                  </FormGroup>
                )}
              </>
            ) : (
              <HorizontalFormRow>
                <FormGroup $compact={compact}>
                  <Label $compact={compact} htmlFor="token-name" className="required">Name</Label>
                  <Input $compact={compact}
                    type="text" 
                    id="token-name" 
                    placeholder="Ex: Super Ink" 
                    maxLength={50}
                    value={formData.name}
                    onChange={handleInputChange}
                    name="name"
                  />
                </FormGroup>
                
                <FormGroup $compact={compact}>
                  <Label $compact={compact} htmlFor="token-ticker" className="required">Symbol</Label>
                  <Input $compact={compact}
                    type="text" 
                    id="token-ticker" 
                    placeholder="Ex: SINK" 
                    maxLength={10}
                    value={formData.symbol}
                    onChange={handleSymbolChange}
                  />
                </FormGroup>
                
                <FormGroup $compact={compact}>
                  <Label $compact={compact} htmlFor="token-category" className="required">Category</Label>
                  <CollapsibleSection>
                    <CollapsibleHeader onClick={() => setIsCategorySectionOpen(!isCategorySectionOpen)}>
                      <span>{formData.category}</span>
                      <CollapsibleIcon isOpen={isCategorySectionOpen}>▼</CollapsibleIcon>
                    </CollapsibleHeader>
                    <CollapsibleContent isOpen={isCategorySectionOpen}>
                      <CollapsibleInner>
                        <CategoryButtonGrid>
                          {(['Meme', 'DeFi', 'Utility', 'Gaming & Metaverse', 'SoFi', 'Creator Coins', 'AI & Tech', 'DAO', 'Experimental/Art', 'Kraken Girls'] as const).map((category) => (
                            <CategoryButton
                              key={category}
                              selected={formData.category === category}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, category }));
                              }}
                              type="button"
                            >
                              {category}
                            </CategoryButton>
                          ))}
                        </CategoryButtonGrid>
                      </CollapsibleInner>
                    </CollapsibleContent>
                  </CollapsibleSection>
                </FormGroup>

                {selectedChain === 'ink' && (
                  <FormGroup $compact={compact}>
                    <Label $compact={compact} htmlFor="dex-selection" className="required">DEX DESTINATION</Label>
                    <VerticalOptionsContainer>
                      {(allowedDex === 'both' || allowedDex === 'protofire') && (
                        <ModeOption $compact={compact}
                          selected={formData.dexSelection === 'protofire'}
                          onClick={() => setFormData(prev => ({ ...prev, dexSelection: 'protofire' }))}
                        >
                          <ModeTitle>
                            Protofire
                          </ModeTitle>
                        </ModeOption>
                      )}
                      {(allowedDex === 'both' || allowedDex === 'inkyswap') && (
                        <ModeOption $compact={compact}
                          selected={formData.dexSelection === 'inkyswap'}
                          onClick={() => setFormData(prev => ({ ...prev, dexSelection: 'inkyswap' }))}
                        >
                          <ModeTitle>InkySwap</ModeTitle>
                        </ModeOption>
                      )}
                    </VerticalOptionsContainer>
                  </FormGroup>
                )}
              </HorizontalFormRow>
            )}

            {/* DEX moved to the first row; this block removed */}

            <CollapsibleSection>
              <CollapsibleHeader onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}>
                <CollapsibleTitle>Description (Optional)</CollapsibleTitle>
                <CollapsibleIcon isOpen={isDescriptionOpen}>▼</CollapsibleIcon>
              </CollapsibleHeader>
              <CollapsibleContent isOpen={isDescriptionOpen}>
                <CollapsibleInner>
                  <FormGroup>
                    <TextArea 
                      id="token-description" 
                      placeholder="Describe your project, its goals, and what makes it stand out!"
                      maxLength={500}
                      value={formData.description}
                      onChange={handleInputChange}
                      name="description"
                    />
                  </FormGroup>
                </CollapsibleInner>
              </CollapsibleContent>
            </CollapsibleSection>

            <CollapsibleSection>
              <CollapsibleHeader onClick={() => setIsSocialLinksOpen(!isSocialLinksOpen)}>
                <CollapsibleTitle>Socials (Optional)</CollapsibleTitle>
                <CollapsibleIcon isOpen={isSocialLinksOpen}>▼</CollapsibleIcon>
              </CollapsibleHeader>
              <CollapsibleContent isOpen={isSocialLinksOpen}>
                <CollapsibleInner>
                  <FormGroup>
                    <Label htmlFor="token-website">Website URL</Label>
                    <Input 
                      type="url" 
                      id="token-website" 
                      placeholder="https://yourproject.com" 
                      value={formData.website}
                      onChange={handleInputChange}
                      name="website"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="token-telegram">Telegram URL</Label>
                    <Input 
                      type="url" 
                      id="token-telegram" 
                      placeholder="https://t.me/yourprojectlink" 
                      value={formData.telegram}
                      onChange={handleInputChange}
                      name="telegram"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="token-twitter">X URL</Label>
                    <Input 
                      type="url" 
                      id="token-twitter" 
                      placeholder="https://x.com/yourprojecthandle" 
                      value={formData.twitter}
                      onChange={handleInputChange}
                      name="twitter"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="token-discord">Discord URL</Label>
                    <Input 
                      type="url" 
                      id="token-discord" 
                      placeholder="https://discord.gg/yourprojectlink" 
                      value={formData.discord}
                      onChange={handleInputChange}
                      name="discord"
                    />
                  </FormGroup>
                </CollapsibleInner>
              </CollapsibleContent>
            </CollapsibleSection>
            
            {/* LP Provider removed - always uses Relay */}
            
            {/* Description and socials are now placed under DEX/Deployment in the grid above */}
            
            {/* Slider CAPTCHA for No-Wallet Deployments (Hidden Mode) */}
            {!isConnected && showCaptcha && !captchaVerified && (
              <SliderCaptcha 
                onSuccess={handleCaptchaSuccess}
                onFail={handleCaptchaFail}
              />
            )}
            
            {statusMessage && (
              <StatusMessage type={statusMessage.type}>
                {statusMessage.text}
              </StatusMessage>
            )}
            
            {enhancedSuccessMessage && (
              <EnhancedSuccessMessage>
                <div style={{ marginBottom: '8px', fontWeight: '600' }}>
                  {enhancedSuccessMessage.tokenName} Successfully deployed!
                </div>
                <div style={{ marginBottom: '8px' }}>
                  View your chart <SuccessLink href={enhancedSuccessMessage.dextoolsUrl} target="_blank" rel="noopener noreferrer">here!</SuccessLink>
                </div>
                <div style={{ fontSize: '12px', color: '#b0b0b0' }}>
                  Token Address: <ContractAddress onClick={() => copyToClipboard(enhancedSuccessMessage.contractAddress)}>{enhancedSuccessMessage.contractAddress}</ContractAddress>
                </div>
              </EnhancedSuccessMessage>
            )}
            <DeployButton 
              disabled={
                isPlatformDeploying || 
                isGaslessDeploying || 
                isSelfFundedDeploying || 
                !formData.name || 
                !formData.symbol ||
                !logoFile // Logo is now required!
              }
              onClick={handleDeployClick}
            >
              {isPlatformDeploying 
                ? 'DEPLOYING...' 
                : isGaslessDeploying 
                ? 'DEPLOYING GASLESS...' 
                : isSelfFundedDeploying 
                  ? 'DEPLOYING...' 
                    : !isConnected
                      ? 'DEPLOY TOKEN'
                  : deploymentMode === 'gasless' 
                    ? 'DEPLOY TOKEN (FREE)' 
                    : 'DEPLOY TOKEN (PAY GAS)'}
            </DeployButton>
          </FormContainer>
        </CreateContainer>
  );

  return (
    <>
      {/* Private Key Modal - Shows after successful temp wallet deployment */}
      <PrivateKeyModal
        isOpen={showPrivateKeyModal}
        privateKey={deploymentPrivateKey}
        walletAddress={deploymentWalletAddress}
        tokenAddress={deploymentTokenAddress}
        onClose={handlePrivateKeyModalClose}
      />
      
      {asPage ? (
        // Render as a full page
        <PageWrapper>
          {formContent}
        </PageWrapper>
      ) : (
        // Render as a modal
        <ModalOverlay onClick={onClose}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <button style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 28,
              cursor: 'pointer',
              zIndex: 10,
            }} onClick={onClose}>&times;</button>
            {formContent}
          </ModalBox>
        </ModalOverlay>
      )}

    {/* First Buy Modal */}
    {showFirstBuyModal && (
      <ModalOverlay style={{ zIndex: 10001 }}>
        <ModalBox>
          <ModalTitle>Be the First Buyer?</ModalTitle>
          <ModalText>
            Would you like to be the first to purchase your token at launch?<br />
            Set your initial buy amount below. You can also skip this step entirely.
          </ModalText>
          <div style={{ width: '100%', marginBottom: 8 }}>
            <label style={{ display: 'block', marginBottom: 12, fontSize: '14px', fontWeight: '600' }}>
              Select Buy Amount (% of Supply):
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr', 
              gap: '12px',
              marginBottom: 12
            }}>
              {[1, 2, 3].map((percentage) => {
                const ethAmount = calculateEthForPercentage(percentage);
                const isSelected = selectedBuyPercentage === percentage;
                return (
                  <button
                    key={percentage}
                    onClick={() => {
                      setSelectedBuyPercentage(percentage as 1 | 2 | 3);
                      setFirstBuyEth(ethAmount);
                    }}
                    style={{
                      padding: '16px 12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: `2px solid ${isSelected ? '#9535fc' : '#444'}`,
                      borderRadius: '8px',
                      background: isSelected ? 'rgba(149, 53, 252, 0.2)' : '#000000',
                      color: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#666';
                        e.currentTarget.style.background = '#333';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#444';
                        e.currentTarget.style.background = '#2a2a2a';
                      }
                    }}
                  >
                    <span style={{ fontSize: '18px', fontWeight: '700', color: isSelected ? '#9535fc' : '#fff' }}>
                      {percentage}%
                    </span>
                    <span style={{ fontSize: '12px', color: '#b0b0b0' }}>
                      {ethAmount.toFixed(6)} ETH
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ 
              width: '100%', 
              marginBottom: 16,
              padding: '10px 12px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '500', textAlign: 'center' }}>
                ⚠️ Maximum 3% of supply at launch to ensure fair and even distribution
              </div>
            </div>
          </div>
          {selectedBuyPercentage && firstBuyEth > 0 && (
            <div style={{ 
              width: '100%', 
              textAlign: 'center', 
              marginBottom: 16,
              padding: '12px',
              background: 'rgba(149, 53, 252, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(149, 53, 252, 0.3)'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                Estimated Tokens You'll Receive:
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#9535fc' }}>
                {estimateFirstBuyTokens(firstBuyEth, selectedBuyPercentage || undefined).toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens
              </div>
              <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: '4px' }}>
                {selectedBuyPercentage}% of supply ({firstBuyEth.toFixed(6)} ETH) • Based on {getMarketCapDisplay()} standard market cap
              </div>
            </div>
          )}
          <ModalButtonRow>
            <ModalButton 
              onClick={() => {
                if (selectedBuyPercentage && firstBuyEth > 0) {
                  handleModalDeploy(true, firstBuyEth);
                }
              }}
              disabled={!selectedBuyPercentage || firstBuyEth <= 0}
            >
              Yes, Buy with Selected Amount
            </ModalButton>
            <ModalCancel onClick={() => {
              setSelectedBuyPercentage(null);
              setFirstBuyEth(0.000001);
              handleModalDeploy(false);
            }}>No, Deploy Without Initial Buy</ModalCancel>
          </ModalButtonRow>
        </ModalBox>
      </ModalOverlay>
    )}

    {/* Share Modal - appears on top of create modal */}
    {shareModalData && (
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onCloseAll={() => {
          setShowShareModal(false);
          onClose(); // Close the create token modal
        }}
        tokenData={shareModalData}
      />
    )}
    </>
  );
};

export default CreateTokenForm;
